import pdfMake from "pdfmake/build/pdfmake"
import pdfFonts from "pdfmake/build/vfs_fonts"

pdfMake.vfs = pdfFonts.vfs

import craigSignature from "../../medias/images/craig.png"
import siriusSquareLogo from "../../medias/images/sirius-square-logo.png"
import { getBase64Image } from "./getBase64Image.js"


/**
 * Generates a Sirius Support "Certificate of Service" PDF document.
 *
 * This function dynamically builds a PDF using pdfMake, embedding:
 * - Company logo (top-left)
 * - Company header information (top-right)
 * - Contractor details table
 * - Official certification statement
 * - CEO signature image
 *
 * Images (logo and signature) are converted to Base64 before rendering.
 *
 * @async
 * @function certificateTemplate
 *
 * @param {string} fullName - Full name of the independent contractor.
 * @param {string} address - Contractor's full address.
 * @param {string} rate - Hourly rate in USD (formatted string, e.g. "$25.00").
 * @param {string} role - Role performed by the contractor.
 * @param {string} dateOfJoining - Date the contractor became active (formatted string).
 * @param {string} dateCreated - Certificate creation date (formatted string).
 *
 * @returns {Promise<pdfMake.TCreatedPdf>} A pdfMake document instance.
 * Use `.download()`, `.open()`, or `.getBlob()` to handle the generated file.
 *
 * @example
 * const pdf = await certificateTemplate(
 *   "John Doe",
 *   "123 Main Street",
 *   "$25.00",
 *   "Software Developer",
 *   "01 January 2023",
 *   "27 February 2026"
 * );
 *
 * pdf.download("certificate-of-service.pdf");
 */
export const certificateTemplate = async(fullName, address, rate, role, dateOfJoining, dateCreated) => {
    const signatureBase64 = await getBase64Image(craigSignature)
    const siriusLogo = await getBase64Image(siriusSquareLogo)
    const docDefinition = {
        pageSize: "A4",
        pageMargins: [40, 100, 40, 80],

        styles: {
        headerRight: {
            fontSize: 10,
            alignment: "right"
        },
        title: {
            fontSize: 18,
            bold: true,
            alignment: "center",
            margin: [0, 40, 0, 30]
        },
        sectionTitle: {
            fontSize: 12,
            bold: true,
            margin: [0, 20, 0, 10]
        },
        normalText: {
            fontSize: 11,
            margin: [0, 5, 0, 5]
        },
        signatureName: {
            bold: true,
            margin: [0, 20, 0, 2]
        },
        smallText: {
            fontSize: 10
        }
        },

        header: {
        margin: [40, 10, 40, 0],
        columns: [
            {
                image: siriusLogo,
                width: 70,
                alignment: "left",
                margin: [0, 5, 0, 0]
            },
            {
                width: "*",
                alignment: "right",
                margin: [0, 30, 0, 0],
                stack: [
                    { text: "85 Great Portland Street" },
                    { text: "London, W1W 7LT" },
                    { 
                        text: "www.sirius-support.com",
                        link: "https://www.sirius-support.com",
                        color: "blue",
                        decoration: "underline"
                    },
                    {  
                        text: "legal@sirius-support.com",
                        link: "mailto:legal@sirius-support.com",
                        color: "blue",
                        decoration: "underline" 
                    }
                ],
                style: "headerRight"
            }
        ]
        },

        content: [

        { text: "CERTIFICATE OF SERVICE", style: "title" },

        { text: dateCreated, style: "normalText" },

        { text: "\nTo whom it may concern,", style: "normalText" },

        {
            text: [
            "This letter is to provide documentation of earnings to ",
            { text: fullName, bold: true },
            " who is an Independent Contractor performing services as a ",
            { text: role, bold: true },
            " for a third-party client through ",
            { text: "SIRIUS SUPPORT LTD.", bold: true }
            ],
            style: "normalText"
        },

        { text: "Independent Contractor Profile", style: "sectionTitle" },

        {
            table: {
                widths: ["35%", "65%"],
                body: [
                [
                    { text: "Name", bold: true },
                    fullName
                ],
                [
                    { text: "Role", bold: true },
                    role
                ],
                [
                    { text: "Active since", bold: true },
                    dateOfJoining
                ],
                [
                    { text: "Address", bold: true },
                    address
                ],
                [
                    { text: "Hourly rate in USD", bold: true },
                    rate
                ]
                ]
            },
            layout: {
                hLineWidth: () => 1,
                vLineWidth: () => 1,
                hLineColor: () => "#000000",
                vLineColor: () => "#000000",
                paddingLeft: () => 8,
                paddingRight: () => 8,
                paddingTop: () => 6,
                paddingBottom: () => 6
            }
        },

        { text: "SIRIUS SUPPORT LTD CERTIFICATION", style: "sectionTitle" },

        {
            text: "This Certification of Service is issued upon the request of the above-mentioned independent contractor for reference purposes, and SIRIUS SUPPORT LTD certifies the truthfulness and authenticity of the same based on company records.",
            style: "normalText"
        },

        {
            image: signatureBase64,
            width: 60,
            margin: [0, 30, 0, 5]
        },
        {
            text: "CRAIG MILLS",
            style: "signatureName"
        },
        {
            text: "CEO / FOUNDER SIRIUS SUPPORT LTD",
            style: "smallText"
        },
        {
            text: "craig@sirius-support.com",
            style: "smallText",
            color: "blue"
        },
        {
            text: "+44 7856 240924",
            style: "smallText"
        }

        ]
    }

     return pdfMake.createPdf(docDefinition)
}